import { memo } from 'react'

import { AnimatedSpinner } from '../../../../components/AnimatedSpinner'
import {
  CheckCircle,
  VideoIcon,
  WarningIcon,
} from '../../../../components/Icons'

import { TwinSpinner } from '../TwinSpinner'
import { ValidationStatus } from './ConsentTab'
import { UpdateSelfie } from './UpdateSelfie'

export interface ValidationUiProps {
  validationStatus: ValidationStatus
  validationError: string
  isMatched: boolean | null
  isRecording: boolean
  isGenerating: boolean
  userName: string
  handleStartRecording: () => void
  handleFinishGeneration: () => void
}

const ValidationUi: React.FC<ValidationUiProps> = memo(
  ({
    validationStatus,
    validationError,
    isMatched,
    isRecording,
    isGenerating,
    userName,
    handleStartRecording,
    handleFinishGeneration,
  }) => {
    return (
      <>
        {(() => {
          switch (validationStatus) {
            case 'uploading':
              return (
                <div className="flex flex-col tc-mv-blur p-4 sm:px-16 sm:py-12 text-lg text-white">
                  <div className="m-auto mb-2 shrink-0">
                    <TwinSpinner />
                  </div>
                  <p className="text-center">
                    Please wait, we are uploading your consent. This will take a
                    few seconds.
                  </p>
                </div>
              )

            case 'validating':
              return (
                <div className="flex flex-col tc-mv-blur p-4 sm:px-16 sm:py-12 text-lg text-white">
                  <div className="m-auto mb-2 shrink-0">
                    <TwinSpinner />
                  </div>
                  <p className="text-center">
                    Now we are validating your consent. This will take 5 to 10
                    seconds.
                  </p>
                </div>
              )

            case 'validated':
              return (
                <>
                  <div className="flex flex-col tc-mv-blur py-4 px-10 sm:px-16 sm:py-12 text-lg text-white w-full">
                    <div className="text-white">
                      <div className="flex justify-center mb-2 h-12">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center bg-white">
                          <div className="text-green-200">
                            <CheckCircle size={48} />
                          </div>
                        </div>
                      </div>
                      <p className="text-center">
                        Hurray! Your consent is valid - you're all set.
                      </p>
                    </div>
                  </div>

                  <div className="text-white mt-auto pt-5 w-full sm:w-auto">
                    <button
                      data-testid="consent-continue-btn"
                      onClick={handleFinishGeneration}
                      className="btn btn-primary mx-auto w-full"
                      disabled={isRecording || isGenerating}
                    >
                      <span className="mr-2">Continue</span>
                      {isGenerating ? (
                        <AnimatedSpinner />
                      ) : (
                        <i className="fa-solid fa-arrow-right text-lg" />
                      )}
                    </button>
                  </div>
                </>
              )

            case 'failed':
              return (
                <>
                  <div className="flex flex-col tc-mv-blur p-4 sm:px-16 sm:py-12 text-lg text-white">
                    <div className="text-white">
                      <div className="flex justify-center mb-2">
                        <div className="bg-red-500 rounded-full w-12 h-12 flex items-center justify-center">
                          <WarningIcon width={24} />
                        </div>
                      </div>{' '}
                      <p className="text-center">
                        {validationError ||
                          "We couldn't detect the correct security code in your recording. Please try again and say it clearly."}
                      </p>
                    </div>
                  </div>
                  <div className="text-white flex flex-col sm:flex-row justify-center gap-4 mt-8 w-full">
                    {isMatched === false && <UpdateSelfie />}

                    <button
                      data-testid="consent-start-recording-btn"
                      onClick={isRecording ? undefined : handleStartRecording}
                      className="btn btn-primary sm:!grow-0 !grow"
                      disabled={isRecording || isGenerating}
                    >
                      {isGenerating ? (
                        <AnimatedSpinner />
                      ) : (
                        <VideoIcon size={16} />
                      )}
                      <span className="ml-2">Start Recording</span>
                    </button>
                  </div>
                </>
              )

            case 'idle':
            default:
              return (
                <>
                  <p className="text-sm sm:text-xl self-start mb-4">
                    Record a quick video reading the following script:
                  </p>
                  <p className="flex items-start mb-0 sm:mb-4 rounded-lg py-2 px-4 sm:p-6 text-base sm:text-xl bg-[#78788C] font-segoe">
                    My name is {userName}. By creating my digital twin, I can
                    create an AI version of myself that can talk to colleagues,
                    fans or clients while I work or sleep. My digital twin
                    belongs only to me, and I can manage or delete it whenever I
                    want. I give Napster permission to use this recording of my
                    voice to create my digital twin, and this voice belongs to
                    me. Napster has no rights to my likeness or voice. My
                    one-time verification code is: XXXXXX
                  </p>{' '}
                  <div className="mt-4 sm:mt-8">
                    <button
                      data-testid="consent-start-recording-idle-btn"
                      onClick={isRecording ? undefined : handleStartRecording}
                      className="btn btn-primary mx-auto w-full sm:w-auto"
                      disabled={isRecording || isGenerating}
                    >
                      <VideoIcon size={16} />
                      <span className="ml-2">Start Recording</span>
                    </button>

                    <div className="mt-2 text-white/50 text-center text-xs font-normal leading-[150%]">
                      You’ll see your verification code once you hit “Start
                      Recording”
                    </div>
                  </div>
                </>
              )
          }
        })()}
      </>
    )
  },
)

export { ValidationUi }
